import { Action, Reducer } from "redux";
import { AppThunkAction } from ".";
import { ApiError } from "types";

import { PaymentTypeApi, PaymentTypeResponse } from "api";

import {
  SetNotificationAction,
  actionCreators as notificationActions,
} from "./Notification";

const paymentTypeApi = new PaymentTypeApi();

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface PaymentTypeState {
  paymentTypeList: PaymentTypeResponse[] | null;
}

enum PaymentTypeActionTypes {
  SET_PAYMENT_TYPE_LIST = "SET_PAYMENT_TYPE_LIST",
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.

export interface SetPaymentTypeListAction {
  type: PaymentTypeActionTypes.SET_PAYMENT_TYPE_LIST;
  payload: PaymentTypeResponse[] | null;
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type KnownAction = SetPaymentTypeListAction | SetNotificationAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const actionCreators = {
  setPaymentTypeList: (
    payload: PaymentTypeResponse[] | null
  ): SetPaymentTypeListAction => ({
    type: PaymentTypeActionTypes.SET_PAYMENT_TYPE_LIST,
    payload,
  }),
  getPaymentTypeList: (): AppThunkAction<KnownAction> => async (dispatch) => {
    try {
      const { data } = await paymentTypeApi.paymentTypeNonCashGet();
      dispatch(actionCreators.setPaymentTypeList(data));
    } catch (error) {
      const { response } = error as { response: ApiError };
      const { data } = response as ApiError;

      dispatch(
        notificationActions.setNotification({
          message: data.title,
          severity: "error",
        })
      );
    }
  },
  createPaymentType:
    (
      name: string,
      onSuccess?: (paymentType?: PaymentTypeResponse) => void,
      onError?: () => void
    ): AppThunkAction<KnownAction> =>
    async (dispatch) => {
      try {
        const { data } = await paymentTypeApi.paymentTypePost({ name });

        if (onSuccess) {
          onSuccess(data);
        }
      } catch (error) {
        const { response } = error as { response: ApiError };
        const { data } = response as ApiError;

        if (onError) {
          onError();
        }

        dispatch(
          notificationActions.setNotification({
            message: data.title,
            severity: "error",
          })
        );
      }
    },
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

const defaultState: PaymentTypeState = {
  paymentTypeList: null,
};

export const reducer: Reducer<PaymentTypeState> = (
  state: PaymentTypeState = defaultState,
  incomingAction: Action
): PaymentTypeState => {
  const action = incomingAction as KnownAction;

  switch (action.type) {
    case PaymentTypeActionTypes.SET_PAYMENT_TYPE_LIST:
      return { ...state, paymentTypeList: action.payload };
    default:
      return state;
  }
};
